import {KpiScore} from '@hconnect/uikit/src/lib2'
import {InfoOutlined} from '@mui/icons-material'
import {Box, Stack, Tooltip} from '@mui/material'
import React from 'react'

import {getTranslationKey} from '../../utils'

import {WorkOrdersOverdueTooltipTitle} from './WorkOrdersOverdueTooltipTitle'
import {WorkOrdersStackedBarChart} from './WorkOrdersStackedBarChart'

import type {AggregatedWorkOrderSummaryDto} from '@hconnect/common/types/workOrders.types'

type Props = {
  data?: AggregatedWorkOrderSummaryDto
  t: (key: string) => string
  translationPrefix?: string
}

export const WorkOrdersSummaryChart = ({data, t, translationPrefix}: Props) => {
  if (!data) return null

  return (
    <Stack spacing={2} useFlexGap sx={{overflowY: 'auto'}}>
      <DataSummary
        total={data.totalWorkOrders}
        overdue={data.overdueWorkOrders}
        totalOverdue={data.totalOverdueWorkOrders}
        closed={data.closedWorkOrders}
        t={t}
        translationPrefix={translationPrefix}
      />
      <WorkOrdersStackedBarChart
        workOrdersSummary={data}
        t={t}
        translationPrefix={translationPrefix}
      />
    </Stack>
  )
}

type DataSummaryProps = {
  total: number
  overdue: number
  totalOverdue: number
  closed: number
  t: (key: string) => string
  translationPrefix?: string
}

const DataSummary = ({
  total,
  overdue,
  totalOverdue,
  closed,
  t,
  translationPrefix
}: DataSummaryProps) => {
  const isOverdueWarningShown = totalOverdue > 0 && overdue !== totalOverdue
  const overdueVariant = totalOverdue > 0 ? 'negative' : 'neutral'

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'space-between'
      }}
    >
      <StackItem>
        <KpiScore
          label={t(getTranslationKey('workOrderSummary.label.total', translationPrefix))}
          value={total}
          data-test-id="total-work-orders"
        />
      </StackItem>
      <StackItem>
        <KpiScore
          label={t(getTranslationKey('workOrderSummary.label.closed', translationPrefix))}
          value={closed}
          data-test-id="closed-work-orders"
        />
      </StackItem>
      <StackItem>
        <Tooltip
          title={
            isOverdueWarningShown ? (
              <WorkOrdersOverdueTooltipTitle
                overdue={overdue}
                totalOverdue={totalOverdue}
                timeLabel={t(getTranslationKey('label.last30days', translationPrefix))}
                translationPrefix={translationPrefix}
              />
            ) : (
              ''
            )
          }
          arrow
          placement="top"
        >
          <span>
            <KpiScore
              label={t(getTranslationKey('workOrderSummary.label.overdue', translationPrefix))}
              labelIcon={isOverdueWarningShown ? <InfoOutlined /> : undefined}
              value={totalOverdue}
              variant={overdueVariant}
              data-test-id="overdue-work-orders"
            />
          </span>
        </Tooltip>
      </StackItem>
    </Stack>
  )
}

const StackItem: React.FC<{children: React.ReactNode}> = ({children}) => (
  <Box
    sx={{
      display: 'flex'
    }}
  >
    {children}
  </Box>
)
